import { stripHtml } from "./slug";

/** Presentation facts only. Unknown discounts/popularity never become claims. */
export interface CardFacts {
  id: string;
  title: string;
  categories?: string[];
  durationMinutes?: number | null;
  priceFrom?: number | null;
  regularPrice?: number | null;
  bookingCount?: number | null;
  featured?: boolean;
  pricingUnit?: "person" | "group" | "vessel" | null;
  cardCancellationPolicy?: string | null;
}

function plain(text: string) {
  return text.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
}

export function tourKind(tour: CardFacts) {
  const text = plain([tour.title, ...(tour.categories ?? [])].join(" "));
  const vessel = /\b(yachts?|yates?|charter|boat|lancha|panga|embarcacion)\b/.test(text);
  const privateService = /\b(privad[oa]s?|private|privat[eo]?|prive|privato|privado|charter)\b/.test(text);
  const yacht = vessel && privateService;
  const days = /\b(\d+)\s*(days?|dias?|jours?|tage|giorni)\b/.exec(text);
  const multiDay = (tour.durationMinutes ?? 0) > 1440 || Number(days?.[1] ?? 0) > 1;
  return { yacht, privateService, multiDay };
}

export function pricingUnit(tour: CardFacts): "person" | "group" | "vessel" {
  if (tour.pricingUnit) return tour.pricingUnit;
  const kind = tourKind(tour);
  return kind.yacht ? "vessel" : kind.privateService ? "group" : "person";
}

/** Whole words, with a sentence boundary preferred in the latter half. */
export function cardSummary(text: string | null | undefined, max = 110): string {
  const clean = stripHtml(text).replace(/\s+/g, " ").trim();
  if (clean.length <= max) return clean;
  const prefix = clean.slice(0, max - 1);
  const sentences = [...prefix.matchAll(/[.!?](?=\s|$)/g)];
  const lastSentence = sentences.at(-1)?.index;
  if (lastSentence != null && lastSentence >= max / 2) return `${prefix.slice(0, lastSentence + 1)}…`;
  const boundary = prefix.search(/\s+\S*$/);
  // A single overlong word cannot be shortened without splitting it.
  if (boundary < 0) return `${clean.split(/\s/)[0]}…`;
  return `${prefix.slice(0, boundary).replace(/[,;:]$/, "").trimEnd()}…`;
}

export type CatalogBadge = "offer" | "bestseller" | "unmissable" | "new";
export function catalogBadges(tours: CardFacts[]): Map<string, CatalogBadge> {
  const result = new Map<string, CatalogBadge>();
  const eligibleCounts = tours.filter((t) => !tourKind(t).privateService && !tourKind(t).multiDay)
    .map((t) => t.bookingCount ?? 0);
  const highest = Math.max(0, ...eligibleCounts);
  // One badged card per COMPLETE block of four, including after filtering.
  for (let start = 0; start + 4 <= tours.length; start += 4) {
    const block = tours.slice(start, start + 4);
    const offer = block.find((t) => t.priceFrom != null && t.regularPrice != null && t.priceFrom < t.regularPrice);
    if (offer) { result.set(offer.id, "offer"); continue; }
    const best = block.find((t) => highest > 0 && t.bookingCount === highest && !tourKind(t).privateService && !tourKind(t).multiDay);
    if (best) { result.set(best.id, "bestseller"); continue; }
    const featured = block.find((t) => t.featured);
    if (featured) result.set(featured.id, "unmissable");
  }
  return result;
}

export function homepageTourOrder<T extends CardFacts>(tours: T[]): T[] {
  const rank = (tour: T) => {
    const kind = tourKind(tour);
    if (kind.privateService || kind.multiDay) return 2;
    return /\b(holbox|bacalar|cozumel|chichen\s*itza|tulum|isla\s*mujeres)\b/.test(plain(tour.title)) ? 0 : 1;
  };
  return [...tours].sort((a, b) => rank(a) - rank(b));
}

export function normalizeSpanishTitle(title: string): string {
  return title.replace(/\b(?:excursi[oó]n|excursion)\s+luxury\s+to\s+/gi, "Excursión de lujo a ");
}

export function needsManualSpanishTitle(title: string): boolean {
  return /\b(private|yacht|charter|luxury|all inclusive|upto|up to|explore|travel|around|days|wonders|dive|timeless|treasures|food tour|from|with|to the|adventure|sailing)\b/i.test(title);
}

const UNIT_COPY: Record<string, Record<"person" | "group" | "vessel", string>> = {
  es: { person: "por adulto", group: "por grupo", vessel: "por embarcación" },
  en: { person: "per adult", group: "per group", vessel: "per vessel" },
  fr: { person: "par adulte", group: "par groupe", vessel: "par bateau" },
  it: { person: "per adulto", group: "per gruppo", vessel: "per imbarcazione" },
  de: { person: "pro Erwachsenem", group: "pro Gruppe", vessel: "pro Boot" },
  pt: { person: "por adulto", group: "por grupo", vessel: "por embarcação" },
};
const UNIT_SHORT: Record<string, Record<"person" | "group" | "vessel", string>> = {
  es: { person: "/ adulto", group: "/ grupo", vessel: "/ embarcación" },
  en: { person: "/ adult", group: "/ group", vessel: "/ vessel" },
  fr: { person: "/ adulte", group: "/ groupe", vessel: "/ bateau" },
  it: { person: "/ adulto", group: "/ gruppo", vessel: "/ imbarcazione" },
  de: { person: "/ Erwachsener", group: "/ Gruppe", vessel: "/ Boot" },
  pt: { person: "/ adulto", group: "/ grupo", vessel: "/ embarcação" },
};
/** Unidad corta para tarjetas pequeñas: "/ adulto", "/ grupo". */
export function priceUnitShort(tour: CardFacts, lang = "es"): string {
  return (UNIT_SHORT[lang.slice(0, 2)] ?? UNIT_SHORT.es)[pricingUnit(tour)];
}
export function priceUnitLabel(tour: CardFacts, lang = "es"): string {
  return (UNIT_COPY[lang.slice(0, 2)] ?? UNIT_COPY.es)[pricingUnit(tour)];
}

/** Only use explicit free-cancellation text, not absence of a policy. */
export function cancellationLabel(tour: CardFacts, lang = "es"): string {
  const policy = stripHtml(tour.cardCancellationPolicy);
  if (policy) return policy;
  const generic: Record<string, string> = {
    es: "Consulta las condiciones de cancelación de esta actividad",
    en: "Check this activity’s cancellation conditions",
    fr: "Consultez les conditions d’annulation de cette activité",
    it: "Consulta le condizioni di cancellazione di questa attività",
    de: "Bitte prüfe die Stornierungsbedingungen dieser Aktivität",
    pt: "Consulte as condições de cancelamento desta atividade",
  };
  return generic[lang.slice(0, 2)] ?? generic.es;
}